/**
 * The row of lab services whose results Lattice reads, shown with each company's own logo.
 *
 * Every logo is drawn in one colour through a CSS mask, the original file supplying only the shape.
 * Shown in their own colours, Labcorp's blue and Quest's green would pull the eye away from the
 * upload above them. Height is set per logo rather than shared, because a bare wordmark like
 * Superpower looks far larger than a stacked mark like Quest's at the same pixel height. The
 * heights here are tuned by eye so the five read as one size.
 *
 * The files are in `public/logos`, taken from each company's site, except Labcorp (Wikimedia
 * Commons) and Quest (Wikipedia), whose sites only serve rasters with a baked-in background.
 */
import * as stylex from "@stylexjs/stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
interface Provider {
  readonly name: string;
  readonly file: string;
  /** Intrinsic width over height of the file, so the box matches the logo exactly. */
  readonly ratio: number;
  /** Rendered height in px. */
  readonly height: number;
}
const LAB_SERVICES: readonly Provider[] = [{
  name: "Function Health",
  file: "/logos/function-health.svg",
  ratio: 201 / 41,
  height: 24
}, {
  name: "Superpower",
  file: "/logos/superpower.svg",
  ratio: 163 / 23,
  height: 17
}, {
  name: "Rythm",
  file: "/logos/rythm.svg",
  ratio: 1160 / 363,
  height: 22
}, {
  name: "Quest Diagnostics",
  file: "/logos/quest-diagnostics.png",
  ratio: 562 / 177,
  height: 32
}, {
  name: "Labcorp",
  file: "/logos/labcorp.svg",
  ratio: 196 / 52,
  height: 25
}];
const _temp = {
  kX1K2I: "xwjwp01",
  kZKoxP: "x16ye13r",
  kzqmXN: "x5lhr3w",
  "$$css": true
};
const styles = {
  logo: {
    k1xSpc: "x1lliihq",
    kWkggS: "x17bqms0",
    kSiTet: "xwq05k6",
    kJcbqH: "x16fucec",
    k2A1W4: "x1rudrqi",
    kn1fP1: "x2k2kcx",
    $$css: true
  },
  artwork: (file: string, ratio: number, height: number) => [_temp, {
    "--x-maskImage": `url("${file}")` != null ? `url("${file}")` : undefined,
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${height}px`),
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${Math.round(height * ratio)}px`)
  }]
};
export function Providers() {
  return <div className="x78zum5 xdt5ytf x1lk1ig7 x1cy8zhl x39sw2z x1nhvcw1 xwaa2ar x1jsi1cl xcmmui4 x1t74ka8 x1q0q8m5 xso031l">
      <div className="x78zum5 xdt5ytf xskm3c9">
        <p className="x1ghz6dp xl4dl4e x1jty25b">Upload results from these services or any other lab</p>
        <p className="x1ghz6dp xl4dl4e x1jty25b">
          Logos name the services only. Lattice is not affiliated with them.
        </p>
      </div>
      <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 x1a02dak x1nhvcw1 x6s0dn4 x7wfn4f xqecrbx" aria-label="Lab services whose results we read">
        {LAB_SERVICES.map(provider => <li key={provider.name} className="x78zum5 x6s0dn4 x10w6t97">
            <span role="img" aria-label={provider.name} data-testid="provider-logo" {...stylex.props(styles.logo, styles.artwork(provider.file, provider.ratio, provider.height))} />
          </li>)}
      </ul>
    </div>;
}